import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import {
    AiOutlineFileExcel,
    AiOutlineFileImage,
    AiOutlineFilePdf,
    AiOutlineFileUnknown,
    AiOutlineFileWord,
} from "react-icons/ai";

import FormComp from "../../../Componentes/FormComp";
import Overlay from "../../../Componentes/Overlay";
import Formulario from "../../../Componentes/Utils/Formulario";
import Modal from "../../../Componentes/Utils/Modal";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    text-align: left;
    width: 100%;
`;

const SecaoAnexos = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 16px;
`;

const AnexosLabel = styled.p`
    margin: 0 0 2px 2px;
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
`;

const SemAnexos = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    text-align: center;
`;

const AnexosCarregando = styled(SemAnexos)`
    color: #475569;
`;

const CardAnexo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background-color: ${(p) => (p.$confirming ? "#fff5f5" : "#f8fafc")};
    border: 1px solid ${(p) => (p.$confirming ? "#fecaca" : "#e2e8f0")};
    border-radius: 10px;
    min-width: 0;
`;

const AnexoIconWrap = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const AnexoInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const AnexoNome = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AnexoTamanho = styled.p`
    margin: 2px 0 0 0;
    font-size: 11px;
    color: #64748b;
`;

const AnexoAcoes = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const BotaoAnexo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
    }
`;

const ConfirmBox = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const ConfirmLabel = styled.span`
    font-size: 11px;
    color: #b91c1c;
    font-weight: 600;
`;

const ConfirmBtn = styled.button`
    border: none;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#b91c1c")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const RESPOSTA_OPCOES = ["C", "OM", "N/A", "NC"];
const RESPOSTAS_COM_OBSERVACAO = ["OM", "NC"];

function getFileIcon(filename) {
    const name = String(filename ?? "").toLowerCase();
    if (/\.(png|jpe?g|gif|webp)$/.test(name)) return <AiOutlineFileImage size={22} color="#4a90e2" />;
    if (name.endsWith(".pdf")) return <AiOutlineFilePdf size={22} color="#e63946" />;
    if (name.endsWith(".doc") || name.endsWith(".docx")) return <AiOutlineFileWord size={22} color="#2a6db0" />;
    if (name.endsWith(".xls") || name.endsWith(".xlsx")) return <AiOutlineFileExcel size={22} color="#207245" />;
    return <AiOutlineFileUnknown size={22} color="#555" />;
}

function formatarTamanho(bytes) {
    if (bytes == null || Number.isNaN(Number(bytes))) return null;
    const n = Number(bytes);
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
    return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

function nomeAnexo(anexo) {
    return anexo?.nome_original || anexo?.caminho?.split("/").pop() || "arquivo";
}

const ModalPreencherItem = ({ item, onSaved }) => {
    const { closeModal } = useModal();
    const api = useApi();
    const { notifyError, notifySuccess } = useToast();

    const [resposta, setResposta] = useState(item?.resposta ?? "");
    const [observacao, setObservacao] = useState(item?.observacao ?? "");
    const [salvando, setSalvando] = useState(false);
    const [anexos, setAnexos] = useState([]);
    const [loadingAnexos, setLoadingAnexos] = useState(true);
    const [arquivosPendentes, setArquivosPendentes] = useState([]);
    const [confirmDeleteAnexoId, setConfirmDeleteAnexoId] = useState(null);
    const [deletingAnexoId, setDeletingAnexoId] = useState(null);

    const exigeObservacao = RESPOSTAS_COM_OBSERVACAO.includes(resposta);

    const itemLabel = useMemo(() => {
        const ordem = item?.ordem_label ?? "";
        const descricao = item?.descricao ?? "";
        if (!ordem && !descricao) return "";
        if (!ordem) return descricao;
        if (!descricao) return ordem;
        return `${ordem} - ${descricao}`;
    }, [item]);

    const carregarAnexos = useCallback(async () => {
        if (!item?.id) {
            setAnexos([]);
            setLoadingAnexos(false);
            return;
        }

        setLoadingAnexos(true);
        try {
            const { data } = await api.get(`/Painel/API/Prossmap/Itens/Get/index.php?id=${item.id}`);
            const payload = data?.data ?? data;
            setAnexos(Array.isArray(payload?.anexos) ? payload.anexos : []);
            if (payload?.resposta) {
                setResposta(payload.resposta);
            }
            setObservacao(payload?.observacao ?? "");
        } catch (err) {
            console.error(err);
            setAnexos([]);
        } finally {
            setLoadingAnexos(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.id]);

    useEffect(() => {
        carregarAnexos();
    }, [carregarAnexos]);

    const handleDeleteAnexo = async (anexoId) => {
        setDeletingAnexoId(anexoId);
        try {
            const { data } = await api.post(
                "/Painel/API/Prossmap/Itens/DeleteAnexo/index.php",
                { id: anexoId }
            );

            if (data?.success) {
                notifySuccess(data?.message || "Anexo removido.");
                setAnexos((prev) => prev.filter((a) => a.id !== anexoId));
                setConfirmDeleteAnexoId(null);
                return;
            }

            notifyError(data?.message || "Não foi possível remover o anexo.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao remover anexo.");
        } finally {
            setDeletingAnexoId(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!item?.id) {
            notifyError("Item inválido.");
            return;
        }

        if (!resposta) {
            notifyError("Selecione uma resposta.");
            return;
        }

        if (exigeObservacao && !observacao.trim()) {
            notifyError("Informe as observações para respostas OM ou NC.");
            return;
        }

        try {
            setSalvando(true);

            const formData = new FormData();
            formData.append("id", item.id);
            formData.append("resposta", resposta);
            if (exigeObservacao) {
                formData.append("observacao", observacao.trim());
            }
            arquivosPendentes.forEach((entry) => formData.append("files[]", entry.file));

            const { data } = await api.post(
                "/Painel/API/Prossmap/Itens/SaveItem/index.php",
                formData
            );

            if (!data?.success) {
                notifyError(data?.message || "Não foi possível salvar o item.");
                return;
            }

            if (Array.isArray(data?.data?.anexos_erros) && data.data.anexos_erros.length > 0) {
                notifySuccess(
                    data?.message || "Item salvo, mas alguns anexos não foram enviados."
                );
            } else {
                notifySuccess(data?.message || "Item salvo com sucesso.");
            }

            setArquivosPendentes([]);
            if (onSaved) await onSaved();
            closeModal();
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao salvar item.";
            notifyError(msg);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Overlay state>
            <Modal header close={closeModal} size="md">
                <ModalTitle>PREENCHER ITEM</ModalTitle>
                <Formulario onSubmit={handleSubmit}>
                    <FormComp
                        title="ITEM"
                        type="text"
                        valor={itemLabel}
                        readOnly
                        disabled
                    />
                    <FormComp
                        title="RESPOSTA"
                        type="select"
                        options={RESPOSTA_OPCOES}
                        valor={resposta}
                        changeFunc={(e) => {
                            const valor = e.target.value;
                            setResposta(valor);
                            if (!RESPOSTAS_COM_OBSERVACAO.includes(valor)) {
                                setObservacao("");
                            }
                        }}
                        required
                    />

                    {exigeObservacao && (
                        <FormComp
                            title="OBSERVAÇÕES"
                            type="textarea"
                            valor={observacao}
                            changeFunc={(e) => setObservacao(e.target.value.toUpperCase())}
                            required
                        />
                    )}

                    <SecaoAnexos>
                        <AnexosLabel>ANEXOS</AnexosLabel>

                        {loadingAnexos ? (
                            <AnexosCarregando>Carregando anexos…</AnexosCarregando>
                        ) : anexos.length === 0 ? (
                            <SemAnexos>Nenhum anexo.</SemAnexos>
                        ) : (
                            anexos.map((anexo) => {
                                const nome = nomeAnexo(anexo);
                                const tam = formatarTamanho(anexo.tamanho);
                                const isConfirming = confirmDeleteAnexoId === anexo.id;
                                const isDeleting = deletingAnexoId === anexo.id;

                                return (
                                    <CardAnexo key={anexo.id ?? anexo.caminho} $confirming={isConfirming}>
                                        <AnexoIconWrap>{getFileIcon(nome)}</AnexoIconWrap>
                                        <AnexoInfo>
                                            <AnexoNome title={nome}>{nome}</AnexoNome>
                                            {tam && <AnexoTamanho>{tam}</AnexoTamanho>}
                                        </AnexoInfo>

                                        {isConfirming ? (
                                            <ConfirmBox>
                                                <ConfirmLabel>Confirmar?</ConfirmLabel>
                                                <ConfirmBtn
                                                    type="button"
                                                    $cancel
                                                    disabled={isDeleting}
                                                    onClick={() => setConfirmDeleteAnexoId(null)}
                                                >
                                                    NÃO
                                                </ConfirmBtn>
                                                <ConfirmBtn
                                                    type="button"
                                                    disabled={isDeleting}
                                                    onClick={() => handleDeleteAnexo(anexo.id)}
                                                >
                                                    {isDeleting ? "…" : "SIM"}
                                                </ConfirmBtn>
                                            </ConfirmBox>
                                        ) : (
                                            <AnexoAcoes>
                                                <BotaoAnexo
                                                    as="a"
                                                    href={anexo.caminho}
                                                    download
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Baixar anexo"
                                                >
                                                    <FiDownload size={14} />
                                                </BotaoAnexo>
                                                <BotaoAnexo
                                                    $danger
                                                    type="button"
                                                    title="Remover anexo"
                                                    onClick={() => setConfirmDeleteAnexoId(anexo.id)}
                                                >
                                                    <FiTrash2 size={14} />
                                                </BotaoAnexo>
                                            </AnexoAcoes>
                                        )}
                                    </CardAnexo>
                                );
                            })
                        )}

                        <MultiFileUploader
                            files={arquivosPendentes}
                            setFiles={setArquivosPendentes}
                        />
                    </SecaoAnexos>

                    <FormComp
                        type="submit"
                        valor={salvando ? "SALVANDO…" : "SALVAR"}
                        disabled={salvando}
                    />
                </Formulario>
            </Modal>
        </Overlay>
    );
};

export default ModalPreencherItem;
